import { useState } from 'react';
import { Image, X, ExternalLink, ZoomIn } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ReceiptViewerProps {
  receiptUrl: string;
}

export function ReceiptViewer({ receiptUrl }: ReceiptViewerProps) {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [imgError, setImgError] = useState(false);

  if (imgError) return null;

  return (
    <>
      {/* Receipt card */}
      <button
        onClick={() => setShowModal(true)}
        className="flex w-full items-center gap-3 rounded-lg bg-surface-secondary p-3 text-left hover:bg-surface-hover transition-colors group"
      >
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
          <Image className="h-5 w-5 text-amber-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-text-secondary">{t('expenses:detail.receiptAttached')}</p>
          <p className="text-xs text-text-tertiary">{t('web:receiptViewer.clickToViewImage')}</p>
        </div>
        <ZoomIn className="h-4 w-4 text-text-quaternary group-hover:text-text-secondary transition-colors shrink-0" />
      </button>

      {/* Lightbox modal */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setShowModal(false)}
        >
          {/* Toolbar */}
          <div className="absolute top-4 right-4 flex gap-2 z-10">
            <a
              href={receiptUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              className="flex items-center gap-1.5 rounded-lg bg-surface-primary/10 px-3 py-2 text-sm text-white hover:bg-surface-primary/20 transition-colors backdrop-blur"
            >
              <ExternalLink className="h-4 w-4" />
              {t('web:receiptViewer.open')}
            </a>
            <button
              onClick={() => setShowModal(false)}
              className="flex items-center justify-center rounded-lg bg-surface-primary/10 p-2 text-white hover:bg-surface-primary/20 transition-colors backdrop-blur"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Image */}
          <img
            src={receiptUrl}
            alt={t('expenses:form.receipt.label')}
            onClick={e => e.stopPropagation()}
            onError={() => { setImgError(true); setShowModal(false); }}
            className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain shadow-2xl"
          />
        </div>
      )}
    </>
  );
}
